import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "../../../lib/api";
import type { SlaInfo } from "../../../types/api";
import { baixarGet } from "../../../lib/download";
import type {
  AbaChamado,
  AnexoChamado,
  Capacidades,
  DetalheChamado,
  ListaChamados,
  LookupsChamado,
  SegmentoChamado,
} from "../../../types/portalCliente";

export interface FiltrosGestao {
  aba: AbaChamado;
  segmento: SegmentoChamado;
  inicio?: string;
  fim?: string;
}

const BASE = "/portal-cliente/chamados";

export const chaves = {
  lista: (filtros: FiltrosGestao) => ["gestao-chamados", filtros] as const,
  detalhe: (id: number) => ["gestao-chamado", id] as const,
  anexos: (id: number) => ["gestao-chamado-anexos", id] as const,
  lookups: ["gestao-chamado-lookups"] as const,
  capacidades: ["portal-cliente-capacidades"] as const,
  severidadesAbertas: ["gestao-severidades-abertas"] as const,
};

/**
 * Mensagem de um erro da API. O corpo é ProblemDetails, onde `detail` explica o
 * caso e `title` resume; a mensagem do axios ("Request failed with status code
 * 400") não diz nada a quem preencheu o formulário.
 */
export const mensagemDeErro = (erro: unknown, padrao: string): string => {
  const resposta = (erro as {
    response?: { status?: number; data?: { detail?: string; title?: string } };
  })?.response;

  // 404 aqui não é "não existe": é a rota de escrita que não foi registrada
  // porque PortalCliente:EscritasHabilitadas está desligada.
  if (resposta?.status === 404 && !resposta?.data?.detail)
    return "A escrita não está liberada neste ambiente.";

  return resposta?.data?.detail ?? resposta?.data?.title ?? padrao;
};

export const useListaChamados = (filtros: FiltrosGestao, habilitado = true) =>
  useQuery({
    enabled: habilitado,
    queryKey: chaves.lista(filtros),
    queryFn: async (): Promise<ListaChamados> => {
      const { data } = await api.get<ListaChamados>(BASE, {
        params: {
          aba: filtros.aba,
          segmento: filtros.segmento,
          inicio: filtros.inicio || undefined,
          fim: filtros.fim || undefined,
        },
      });
      return data;
    },
    staleTime: 60_000,
    // Trocar de aba ou de tipo mantém a lista anterior no lugar enquanto a nova
    // chega. Sem isto a tela inteira — cards, contadores e resumos — sumia e
    // voltava a cada clique de filtro.
    placeholderData: keepPreviousData,
  });

/** Capacidades do ambiente; muda por configuração, não por uso. */
export const useCapacidades = () =>
  useQuery({
    queryKey: chaves.capacidades,
    queryFn: async (): Promise<Capacidades> => {
      const { data } = await api.get<Capacidades>("/portal-cliente/capacidades");
      return data;
    },
    staleTime: 5 * 60_000,
  });

export const useLookupsChamado = (habilitado = true) =>
  useQuery({
    queryKey: chaves.lookups,
    queryFn: async (): Promise<LookupsChamado> => {
      const { data } = await api.get<LookupsChamado>(`${BASE}/lookups`);
      return data;
    },
    staleTime: 5 * 60_000,
    enabled: habilitado,
  });

export interface NovoChamado {
  idCategoria: string;
  idProjeto: string;
  severidade: string;
  assunto: string;
  descricao: string;
  nomeContato: string;
  emailContato: string;
  receberInteracaoPorEmail: boolean;
  anexos: File[];
}

/**
 * Abre o chamado. O envio é multipart porque os anexos vão na mesma
 * requisição.
 *
 * O Content-Type precisa ser dito aqui: a instância do axios tem
 * `application/json` como padrão e, com esse cabeçalho, o transformRequest
 * converte o FormData em JSON (`formDataToJSON`) — os arquivos somem e a API
 * recusa com "Formato inválido". Com multipart declarado, o FormData passa
 * inteiro e o próprio axios troca o cabeçalho pelo que traz o boundary.
 */
export const useCriarChamado = () => {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: async (dados: NovoChamado): Promise<number> => {
      const corpo = new FormData();
      corpo.append("idCategoria", dados.idCategoria);
      corpo.append("idProjeto", dados.idProjeto);
      corpo.append("severidade", dados.severidade);
      corpo.append("assunto", dados.assunto);
      corpo.append("descricao", dados.descricao);
      corpo.append("nomeContato", dados.nomeContato);
      corpo.append("emailContato", dados.emailContato);
      corpo.append(
        "receberInteracaoPorEmail",
        dados.receberInteracaoPorEmail ? "true" : "false"
      );
      dados.anexos.forEach((arquivo) => corpo.append("anexos", arquivo));

      const { data } = await api.post<{ id: number }>(BASE, corpo, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.id;
    },
    onSuccess: () => {
      // A lista e os contadores mudam com o chamado novo.
      clienteQuery.invalidateQueries({ queryKey: ["gestao-chamados"] });
    },
  });
};

/**
 * A SLA do chamado, pela mesma rota e o mesmo cálculo do quadro (ficha, 05/10/2026). Atualiza a cada
 * minuto: o tempo restante anda com a tela aberta.
 */
export const useSlaChamado = (id: number, habilitado = true) =>
  useQuery({
    queryKey: ["chamado", id, "sla"],
    queryFn: async (): Promise<SlaInfo> => (await api.get<SlaInfo>(`/chamados/${id}/sla`)).data,
    enabled: habilitado && Number.isFinite(id) && id > 0,
    refetchInterval: 60_000,
    retry: false,
  });

/** @param habilitado o quadro só busca o detalhe quando a etiqueta do cartão é aberta. */
export const useDetalheChamado = (id: number, habilitado = true) =>
  useQuery({
    queryKey: chaves.detalhe(id),
    queryFn: async (): Promise<DetalheChamado> => {
      const { data } = await api.get<DetalheChamado>(`${BASE}/${id}`);
      return data;
    },
    enabled: habilitado && Number.isFinite(id) && id > 0,
  });

/** `habilitado`: o clipe do Kanban só busca quando abre (07/10/2026). */
export const useAnexosChamado = (id: number, habilitado = true) =>
  useQuery({
    queryKey: chaves.anexos(id),
    queryFn: async (): Promise<AnexoChamado[]> => {
      const { data } = await api.get<AnexoChamado[]>(`${BASE}/${id}/anexos`);
      return data;
    },
    enabled: habilitado && Number.isFinite(id) && id > 0,
  });

export interface GrupoReordenacao {
  severidade: number;
  chamadoIds: number[];
}

/**
 * Grava severidade e ordem da aba Novas Solicitações. O lote precisa conter
 * *todos* os chamados da aba — a prioridade é a posição dentro do grupo, então
 * um lote parcial embaralharia quem ficou de fora. O servidor confere o
 * conjunto dentro da transação e devolve 409 quando ele não bate, que é o caso
 * de alguém ter aberto ou cancelado um chamado nesse meio tempo.
 */
export const useReordenarChamados = () => {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: async (grupos: GrupoReordenacao[]) => {
      const { data } = await api.post<{ atualizados: number; inalterados: number }>(
        `${BASE}/reordenar`,
        { grupos }
      );
      return data;
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["gestao-chamados"] });
    },
  });
};

export interface SeveridadeAberta {
  severidade: number;
  aberta: boolean;
}

const TODAS_ABERTAS: SeveridadeAberta[] = [0, 1, 2, 3].map((severidade) => ({
  severidade,
  aberta: true,
}));

/**
 * Quais grupos de severidade da aba Novas a pessoa deixa abertos, gravado por
 * pessoa como na origem. Sem a rota (API antiga), a consulta falha calada e o
 * quadro abre tudo.
 */
export const useSeveridadesAbertas = () =>
  useQuery({
    queryKey: chaves.severidadesAbertas,
    queryFn: async (): Promise<SeveridadeAberta[]> => {
      const { data } = await api.get<{ severidades: SeveridadeAberta[] }>(
        `${BASE}/severidades-abertas`
      );
      return data.severidades;
    },
    staleTime: Infinity,
    retry: false,
  });

/**
 * Grava um grupo. O cache muda antes da resposta, para que voltar à aba já
 * encontre o grupo como a pessoa deixou; se a gravação falhar, o cache é
 * relido do servidor na próxima vez.
 */
export const useLembrarSeveridadeAberta = () => {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: async ({ severidade, aberta }: SeveridadeAberta) => {
      await api.put(`${BASE}/severidades-abertas/${severidade}`, { aberta });
    },
    onMutate: ({ severidade, aberta }) => {
      clienteQuery.setQueryData<SeveridadeAberta[]>(chaves.severidadesAbertas, (atual) =>
        (atual ?? TODAS_ABERTAS).map((s) => (s.severidade === severidade ? { ...s, aberta } : s))
      );
    },
    onError: () => {
      clienteQuery.invalidateQueries({ queryKey: chaves.severidadesAbertas });
    },
  });
};

/**
 * Baixa o anexo pelo cliente HTTP autenticado. Link direto não serve: a sessão
 * é por token no cabeçalho, e não por cookie, então o navegador não o enviaria.
 */
export const baixarAnexo = async (
  idChamado: number,
  anexo: AnexoChamado
): Promise<void> => {
  const { data } = await api.get<Blob>(
    `${BASE}/${idChamado}/anexos/${anexo.id}`,
    { params: { download: true }, responseType: "blob" }
  );

  const url = URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = url;
  link.download = anexo.denominacao ?? `anexo-${anexo.id}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export interface ExecucaoAcao {
  idChamado: number;
  acao: string;
  motivo?: string;
}

/**
 * Aprovar, recusar, reabrir, cancelar ou finalizar. O servidor confere de novo
 * se a ação vale para o status atual — entre carregar a tela e clicar, alguém
 * da equipe pode ter mexido no chamado — e responde 400 dizendo quais valem.
 */
export const useExecutarAcao = () => {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: async ({ idChamado, acao, motivo }: ExecucaoAcao) => {
      await api.post(`${BASE}/${idChamado}/acoes`, { acao, motivo });
    },
    onSettled: (_dados, _erro, { idChamado }) => {
      // Vale também no erro: se a recusa foi por status defasado, a tela precisa
      // mostrar o estado de agora.
      clienteQuery.invalidateQueries({ queryKey: chaves.detalhe(idChamado) });
      clienteQuery.invalidateQueries({ queryKey: ["gestao-chamados"] });
      // A ação também sai da etiqueta do cartão no quadro.
      clienteQuery.invalidateQueries({ queryKey: ["chamados", "kanban"] });
      clienteQuery.invalidateQueries({ queryKey: ["chamado", idChamado] });
    },
  });
};

export interface NovaInteracao {
  idChamado: number;
  comentario: string;
  anexos: File[];
  /** Nota interna, só da equipe T4i. A API ignora o campo vindo do cliente. */
  interna?: boolean;
}

/**
 * Comentário do cliente no chamado, com anexos na mesma requisição. Multipart
 * pelo mesmo motivo da abertura — e com o mesmo cuidado: sem declarar o tipo, o
 * axios converteria o FormData em JSON e os arquivos sumiriam.
 */
export const useCriarInteracao = () => {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: async ({ idChamado, comentario, anexos, interna }: NovaInteracao) => {
      const corpo = new FormData();
      corpo.append("comentario", comentario);
      if (interna) corpo.append("interna", "true");
      anexos.forEach((arquivo) => corpo.append("anexos", arquivo));

      const { data } = await api.post<{ id: number }>(
        `${BASE}/${idChamado}/interacoes`,
        corpo,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data.id;
    },
    onSuccess: (_id, { idChamado }) => {
      // A interação pode mudar o status do chamado e traz anexos novos: as três
      // consultas da tela precisam ser refeitas.
      clienteQuery.invalidateQueries({ queryKey: chaves.detalhe(idChamado) });
      clienteQuery.invalidateQueries({ queryKey: chaves.anexos(idChamado) });
      clienteQuery.invalidateQueries({ queryKey: ["gestao-chamados"] });
      // A interação rápida do quadro: o cartão e a janela do chamado também se refazem.
      clienteQuery.invalidateQueries({ queryKey: ["chamados", "kanban"] });
      clienteQuery.invalidateQueries({ queryKey: ["chamado", idChamado] });
    },
  });
};

/**
 * Conteúdo do anexo para exibir na tela. Sem `download`, a API devolve o tipo
 * real quando ele está na lista de exibíveis (imagens e PDF) e
 * application/octet-stream no resto — é esse tipo que decide como mostrar.
 *
 * Link direto não serve: a sessão é por cabeçalho, não por cookie.
 */
export const abrirAnexo = async (
  idChamado: number,
  anexo: AnexoChamado
): Promise<{ url: string; tipo: string }> => {
  const { data } = await api.get<Blob>(`${BASE}/${idChamado}/anexos/${anexo.id}`, {
    responseType: "blob",
  });
  return { url: URL.createObjectURL(data), tipo: data.type };
};

/** Exporta exatamente o recorte que está na tela. */
export const exportarChamados = (
  filtros: FiltrosGestao,
  formato: "pdf" | "excel"
): Promise<void> =>
  baixarGet(
    `${BASE}/relatorio`,
    {
      aba: filtros.aba,
      segmento: filtros.segmento,
      inicio: filtros.inicio || undefined,
      fim: filtros.fim || undefined,
      formato,
    },
    `chamados-${filtros.aba.toLowerCase()}.${formato === "excel" ? "xlsx" : "pdf"}`
  );
